///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Creating share links. Signed-in users get a server-backed short URL
/// (?s=<slug>) whose snapshot a recipient can open with no account —
/// including placemarks, which only exist in the owner's state and are
/// unreachable through plain deep-link params. Signed out (or on any
/// server error) the old self-contained param URL is the fallback, so
/// copy-link never fails outright.
///

import { useCreateShareMutation, shareUrlForSlug } from "@/redux/sharesApi"
import type { Placemark } from "@/redux/placemarksSlice"
import type { SavedView } from "@/redux/savedViewsSlice"
import type { Story } from "@/redux/storiesSlice"
import { storyToSharePayload } from "@/redux/storiesApi"
import { useAppSelector, useAppStore } from "@/redux/store"
import { autoNameForCamera, currentViewSnapshot, featureShareUrl, viewShareUrl } from "@/utils/savedViews"
import { parseTarget } from "@/utils/navigationHistory"
import type { SharePlacemarkPayload, ShareViewPayload } from "@shared-types"
import { useCallback } from "react"

export function useShareLink() {
	const [createShare] = useCreateShareMutation()
	const signedIn = useAppSelector(state => !!state.user.userProfile)
	const store = useAppStore()

	/** Short link for a saved view (falls back to the long param URL) */
	const shareView = useCallback(async (view: SavedView): Promise<string> => {
		if (!signedIn) return viewShareUrl(view)
		try {
			const payload: ShareViewPayload = {
				camera: { ...view.camera },
				layers: view.layers && view.layers.length > 0 ? [...view.layers] : undefined,
				focus_target_id: view.focusTargetId ?? undefined,
			}
			const result = await createShare({ share_type: "view", title: view.name, payload }).unwrap()
			return shareUrlForSlug(result.share.slug)
		} catch (error) {
			console.warn("[shares] Falling back to param URL:", error)
			return viewShareUrl(view)
		}
	}, [signedIn, createShare])

	/** Short link carrying a full placemark snapshot. Null when signed out —
	 *  a param URL can't represent a placemark the recipient doesn't have. */
	const sharePlacemark = useCallback(async (placemark: Placemark): Promise<string | null> => {
		if (!signedIn) return null
		try {
			const payload: SharePlacemarkPayload = {
				placemark: {
					id: placemark.id,
					name: placemark.name,
					color: placemark.color,
					note: placemark.note,
					anchor_id: placemark.anchorId,
					locations: placemark.locations.map(l => ({
						id: l.id,
						lat: l.lat,
						lon: l.lon,
						name: l.name,
						altitudeM: l.altitudeM,
						note: l.note,
					})),
				},
			}
			const result = await createShare({ share_type: "placemark", title: placemark.name, payload }).unwrap()
			return shareUrlForSlug(result.share.slug)
		} catch (error) {
			console.warn("[shares] Placemark share failed:", error)
			return null
		}
	}, [signedIn, createShare])

	/** Short link carrying a full story snapshot. Null when signed out or
	 *  the story has no stops — a param URL can't represent a story the
	 *  recipient doesn't have. */
	const shareStory = useCallback(async (story: Story): Promise<string | null> => {
		if (!signedIn || story.stops.length === 0) return null
		try {
			const result = await createShare({
				share_type: "story",
				title: story.title,
				payload: storyToSharePayload(story),
			}).unwrap()
			return shareUrlForSlug(result.share.slug)
		} catch (error) {
			console.warn("[shares] Story share failed:", error)
			return null
		}
	}, [signedIn, createShare])

	/**
	 * Short link for the currently focused feature (?target=). Placemark
	 * targets get a snapshot share (recipients don't have the placemark);
	 * other entities get a view share focused on the entity. Falls back to
	 * the long param URL; null when nothing is focused.
	 */
	const shareFocusedFeature = useCallback(async (): Promise<string | null> => {
		const target = new URL(window.location.href).searchParams.get("target")
		if (!target) return null
		if (!signedIn) return featureShareUrl()

		const state = store.getState()
		const parsed = parseTarget(target)

		if (parsed?.kind === "entity" && parsed.type === "placemark") {
			// pin-<uuid> or pin-<uuid>:<memberId> -> snapshot of the placemark
			const placemarkId = parsed.id.split(":")[0]
			const placemark = state.placemarks.placemarks[placemarkId]
			if (placemark) {
				const url = await sharePlacemark(placemark)
				if (url) return url
			}
			return featureShareUrl()
		}

		try {
			const title = state.userInterface.infoWindow.isOpen
				? state.userInterface.infoWindow.title.trim() || target
				: target
			const payload: ShareViewPayload = {
				camera: { ...state.earthbrowser.cameraState },
				layers: parsed?.kind === "entity" ? [parsed.type] : undefined,
				focus_target_id: target,
			}
			const result = await createShare({ share_type: "view", title, payload }).unwrap()
			return shareUrlForSlug(result.share.slug)
		} catch (error) {
			console.warn("[shares] Falling back to param URL:", error)
			return featureShareUrl()
		}
	}, [signedIn, store, createShare, sharePlacemark])

	/**
	 * Link for whatever is on screen right now: the focused feature when
	 * there is one (so the recipient gets the entity framed, placemarks
	 * included), otherwise the live camera + visible layers. Never fails:
	 * signed-out and error paths produce the self-contained param URL.
	 */
	const shareCurrentView = useCallback(async (): Promise<string> => {
		const focused = await shareFocusedFeature()
		if (focused) return focused

		const snapshot = currentViewSnapshot()
		if (!signedIn) return viewShareUrl(snapshot)
		try {
			const payload: ShareViewPayload = {
				camera: snapshot.camera,
				layers: snapshot.layers && snapshot.layers.length > 0 ? snapshot.layers : undefined,
			}
			const title = `View over ${await autoNameForCamera(snapshot.camera)}`
			const result = await createShare({ share_type: "view", title, payload }).unwrap()
			return shareUrlForSlug(result.share.slug)
		} catch (error) {
			console.warn("[shares] Falling back to param URL:", error)
			return viewShareUrl(snapshot)
		}
	}, [signedIn, createShare, shareFocusedFeature])

	return { shareView, sharePlacemark, shareStory, shareFocusedFeature, shareCurrentView }
}
